/** @jsx jsx */
import { createContext, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { jsx } from '@emotion/core';
import { useTranslation } from 'react-i18next';

import {
  ChallengeDatesType,
  ChallengePhaseType,
  ChallengeResultType,
  IChallengesPartial,
  LoadingStatusType,
} from 'interfaces';
import { getMediaObjectURI } from 'services/api';
import { Banner, CenteredLoader } from 'components';
import {
  challengeSelector,
  challengeListUserResult,
} from 'reducers/challenge.reducer';
import { productCurrencySelector } from 'reducers/app.reducer';
import { AccessDenied } from 'components';
import {
  Container,
  PageContainer,
} from 'components/ChallengeDetailsContainers';
import { getFormatedDates } from 'core/utils/date.helpers';
import { getChallengePhase } from 'core/utils/challenges.helpers';
import { LOADING_STATUS } from 'constants/status';
import DescriptionSection from './DescriptionSection';
import GeneralRankingSection from './GeneralRankingSection';
import ResultSection from './ResultSection';
import ResultPerProductSection from './ResultPerProductSection';
import ProductsAndGainsSection from './ProductsAndGainsSection';
import GoalsAndRewardsSection from './GoalsAndRewardsSection';
import { GOAL, CHALLENGE_PHASE } from 'constants/challenges';

type ChallengeListDetailsContextType = {
  challenge: IChallengesPartial;
  productCurrency: string | null;
  currentPhase: ChallengePhaseType | null;
  challengeResult:
    | {
        resultValue: number;
        rank: number;
      }
    | ChallengeResultType
    | undefined;
  challengeStatus: LoadingStatusType;
};

export const ChallengeListDetailsContext =
  createContext<ChallengeListDetailsContextType>(undefined!);

interface IChallengeListDetails {
  challengeId: number;
}

export default function DetailsChallengeList({
  challengeId,
}: IChallengeListDetails): JSX.Element {
  const { data: challenge, status: challengeStatus } = useSelector(
    challengeSelector(challengeId),
  );
  const challengeResult = useSelector(challengeListUserResult(challengeId));
  const productCurrency = useSelector(productCurrencySelector);
  const {
    i18n: { language },
  } = useTranslation();

  if (challengeStatus !== LOADING_STATUS.RESOLVED) {
    return <CenteredLoader />;
  }

  const dates: ChallengeDatesType = getFormatedDates(
    {
      challenge: {
        start: challenge.startDate,
        end: challenge.endDate,
      },
      diffusion: {
        start: challenge.startDiffusionDate,
        end: challenge.endDiffusionDate,
      },
    },
    { day: 'numeric', month: 'short', year: 'numeric' },
    language,
  );
  const currentPhase = getChallengePhase(dates);

  if (
    currentPhase === CHALLENGE_PHASE.BEFORE_DIFFUSION ||
    currentPhase === CHALLENGE_PHASE.AFTER_DIFFUSION
  ) {
    return <AccessDenied />;
  }

  const hasChallengeStarted =
    currentPhase === CHALLENGE_PHASE.DURING_CHALLENGE ||
    currentPhase === CHALLENGE_PHASE.AFTER_CHALLENGE;

  const isChallengeEnded = currentPhase === CHALLENGE_PHASE.AFTER_CHALLENGE;

  return (
    <Container>
      <Banner
        challengeType={challenge.type}
        dates={dates}
        half={false}
        image={getMediaObjectURI(challenge.image)}
        isParticipating={true}
      />

      <ChallengeListDetailsContext.Provider
        value={{
          currentPhase,
          challenge,
          productCurrency,
          challengeResult,
          challengeStatus,
        }}
      >
        <PageContainer>
          <DescriptionSection />
          {challenge.goalType !== GOAL.RESULT && <GoalsAndRewardsSection />}
          <ProductsAndGainsSection />
          {hasChallengeStarted && (
            <Fragment>
              <ResultSection />
              <ResultPerProductSection />
              {challenge.areResultsVisible && <GeneralRankingSection isChallengeEnded={isChallengeEnded} />}
            </Fragment>
          )}
        </PageContainer>
      </ChallengeListDetailsContext.Provider>
    </Container>
  );
}
